import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  TextField,
  Typography,
  Button,
  Stack,
  DialogTitle,
  Dialog,
  DialogContent,
  DialogActions,
  Paper,
  Divider,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { getMultipartHeader } from "../utility/utility";
import axios from "axios";
import { toastError } from "../utils2024/utils";
import { API_ENDPOINT } from "../constants/constants";
import { LoadingButton } from "@mui/lab";
import BackBtn from "../widgets/BackBtn";

const SupportRequest = () => {
  const [message, setMessage] = useState("");
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [successDialog, setSuccessDialog] = useState(false);

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setImage(file);

      // Create a preview URL for the selected image
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setImage(null);
    setImagePreview(null);
  };

  const handleSubmit = async () => {
    if (!message.trim()) {
      toastError("Please describe your issue before submitting.");
      return;
    }

    const formData = new FormData();
    formData.append("message", message);
    if (image) {
      formData.append("image", image);
    }
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API_ENDPOINT}/user/request-support`,
        formData,
        getMultipartHeader()
      );
      setSuccessDialog(true);
    } catch (error) {
      toastError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDialogClose = () => {
    setSuccessDialog(false);
    document.location.reload();
  };

  return (
    <Box sx={{ maxWidth: 1200, margin: "0 auto", mt: 2, mb: 4, px: 2 }}>
      <BackBtn />
      <Box
        sx={{
          maxWidth: 650,
          margin: "auto",
          mt: 3,
        }}
      >
        <Paper
          elevation={0}
          variant="outlined"
          sx={{ borderRadius: 3, overflow: "hidden" }}
        >
          <Box
            sx={{
              bgcolor: "#f8fafc",
              p: 3,
              borderBottom: "1px solid #e2e8f0",
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Box
              sx={{
                bgcolor: "primary.main",
                color: "white",
                borderRadius: "12px",
                width: 48,
                height: 48,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
              }}
            >
              <SupportAgentIcon fontSize="medium" />
            </Box>
            <Box>
              <Typography variant="h5" fontWeight="700" color="#1e293b">
                Support Request
              </Typography>
              <Typography variant="body2" color="text.secondary">
                We're here to help! Let us know what you need.
              </Typography>
            </Box>
          </Box>

          <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
            <Stack spacing={4}>
              <Box>
                <Typography
                  variant="subtitle1"
                  fontWeight="600"
                  gutterBottom
                  color="#334155"
                >
                  Describe your issue
                </Typography>
                <TextField
                  placeholder="Please provide as much detail as possible so we can assist you better..."
                  variant="outlined"
                  multiline
                  rows={5}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  fullWidth
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      backgroundColor: "#fcfcfc",
                    },
                  }}
                />
              </Box>

              <Divider />

              <Box>
                <Typography
                  variant="subtitle1"
                  fontWeight="600"
                  gutterBottom
                  color="#334155"
                >
                  Attachments (Optional)
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={2}>
                  Upload a screenshot or image that might help us understand the
                  problem.
                </Typography>

                {!imagePreview ? (
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<UploadFileIcon />}
                    sx={{
                      borderRadius: 2,
                      textTransform: "none",
                      borderStyle: "dashed",
                      borderWidth: 2,
                      py: 2,
                      width: "100%",
                      color: "text.secondary",
                      borderColor: "#cbd5e1",
                      "&:hover": {
                        borderStyle: "dashed",
                        borderWidth: 2,
                        backgroundColor: "#f8fafc",
                      },
                    }}
                  >
                    Click to browse and upload an image
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={handleImageChange}
                    />
                  </Button>
                ) : (
                  <Box
                    sx={{
                      position: "relative",
                      display: "inline-block",
                      mt: 1,
                    }}
                  >
                    <img
                      src={imagePreview}
                      alt="Preview"
                      style={{
                        maxWidth: "100%",
                        maxHeight: "200px",
                        borderRadius: "8px",
                        border: "1px solid #e2e8f0",
                      }}
                    />
                    <Box
                      sx={{
                        mt: 1,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          maxWidth: 200,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {image.name}
                      </Typography>
                      <Button
                        size="small"
                        color="error"
                        onClick={removeImage}
                        sx={{ textTransform: "none" }}
                      >
                        Remove
                      </Button>
                    </Box>
                  </Box>
                )}
              </Box>

              <Box sx={{ display: "flex", justifyContent: "flex-end", pt: 2 }}>
                <LoadingButton
                  loading={loading}
                  variant="contained"
                  color="primary"
                  onClick={handleSubmit}
                  size="large"
                  sx={{
                    borderRadius: 2,
                    px: 4,
                    py: 1.2,
                    textTransform: "none",
                    fontWeight: 600,
                    boxShadow: "0 4px 14px rgba(0,0,0,0.15)",
                  }}
                >
                  Submit Request
                </LoadingButton>
              </Box>
            </Stack>
          </CardContent>
        </Paper>
      </Box>

      {/* Success Dialog */}
      <Dialog
        open={successDialog}
        onClose={handleDialogClose}
        PaperProps={{
          sx: { borderRadius: 3, p: 2, textAlign: "center", maxWidth: 400 },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <CheckCircleOutlineIcon
            color="success"
            sx={{ fontSize: 64, mb: 1 }}
          />
          <Typography variant="h5" fontWeight="700" color="#1e293b">
            Request Submitted!
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography color="text.secondary">
            Your support request has been successfully submitted. Our team will
            get back to you shortly.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pt: 2 }}>
          <Button
            onClick={handleDialogClose}
            variant="contained"
            color="primary"
            sx={{
              px: 4,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SupportRequest;
